import { useEffect, useMemo, useState } from "react";
import { Phone, Mail, MapPin, Eye, Pencil, Trash2, Sparkles, Plus } from "lucide-react";
import { fetchCustomers, fetchJobs, fetchInvoices, deleteCustomer, formatApiError } from "@/lib/api";
import { PageHeader, RangeToggle, useRange, fmtKr } from "@/components/dashboard/Primitives";
import SearchInput from "@/components/dashboard/SearchInput";
import CustomerDetailModal from "@/components/dashboard/CustomerDetailModal";
import CustomerFormDialog from "@/components/dashboard/CustomerFormDialog";
import DataSourceBadge from "@/components/dashboard/DataSourceBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Customers() {
    const [customers, setCustomers] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [range, setRange] = useRange("month");
    const [search, setSearch] = useState("");
    const [openCustomer, setOpenCustomer] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editCustomer, setEditCustomer] = useState(null);

    const refresh = () => {
        fetchCustomers().then((d) => setCustomers(d || [])).catch(() => {});
        fetchJobs().then((d) => setJobs(d || [])).catch(() => {});
        fetchInvoices().then((d) => setInvoices(Array.isArray(d) ? d : (d?.items || d?.fakturaer || []))).catch(() => {});
    };

    useEffect(() => { refresh(); }, []);

    const customersWithInvoices = useMemo(() => {
        const map = {};
        invoices.forEach((inv) => { (map[inv.customer_id || ""] = map[inv.customer_id || ""] || []).push(inv); });
        return customers.map((c) => ({ ...c, invoices: map[c.id] || [] }));
    }, [customers, invoices]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        if (!q) return customersWithInvoices;
        return customersWithInvoices.filter((c) =>
            [c.id, c.name, c.email, c.phone, c.address].some((v) => (v || "").toString().toLowerCase().includes(q))
        );
    }, [customersWithInvoices, search]);

    const jobDetails = useMemo(() => {
        const map = {};
        jobs.forEach((j) => { map[j.id] = j; });
        return map;
    }, [jobs]);

    const onDelete = async (id, name) => {
        if (!window.confirm(`Slet kunde ${name}?`)) return;
        try {
            await deleteCustomer(id);
            toast.success("Kunde slettet");
            refresh();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const openEdit = (c) => { setEditCustomer(c); setFormOpen(true); };

    return (
        <div data-testid="customers-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <PageHeader
                title="Customers"
                subtitle="Kunder oprettes automatisk når et job kommer ind. Klik for at se historik."
                right={
                    <div className="flex items-center gap-3">
                        <DataSourceBadge entity="customers" />
                        <SearchInput value={search} onChange={setSearch} placeholder="Søg kunde…" testid="customers-search" />
                        <RangeToggle value={range} onChange={setRange} testid="customers-range" />
                        <Button data-testid="customer-new-btn" onClick={() => { setEditCustomer(null); setFormOpen(true); }}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white h-9">
                            <Plus className="w-4 h-4 mr-1" /> Ny kunde
                        </Button>
                    </div>
                }
            />

            <div className="mb-4 bg-blue-50/60 border border-blue-100 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-blue-900">
                <Sparkles className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                <p>Kunderne opdateres automatisk fra jobs, fakturaer og dataflowet. Listen viser kundernes historik og omsætning.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filtered.map((c) => (
                    <div
                        key={c.id}
                        data-testid={`customer-${c.id}`}
                        onClick={() => setOpenCustomer(c)}
                        className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">{c.name || "Ukendt"}</h3>
                                <p className="text-xs text-slate-400 font-mono mt-0.5">{c.id}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-slate-400 uppercase tracking-wider">Omsætning</p>
                                <p className="text-lg font-bold text-emerald-600">{fmtKr(c.revenue)}</p>
                            </div>
                        </div>

                        <div className="mt-4 space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-slate-600"><Phone className="w-4 h-4 text-slate-400" />{c.phone || "—"}</div>
                            <div className="flex items-center gap-2 text-slate-600"><Mail className="w-4 h-4 text-slate-400" />{c.email || "—"}</div>
                            <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-slate-400" />{c.address || "—"}</div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                            <span className="text-slate-500">Seneste job: <span className="text-slate-800 font-medium">{c.last_job || "—"}</span></span>
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                <Button size="sm" variant="outline" data-testid={`customer-edit-${c.id}`} onClick={() => openEdit(c)} className="h-7 text-xs">
                                    <Pencil className="w-3 h-3 mr-1" /> Redigér
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`customer-delete-${c.id}`} onClick={() => onDelete(c.id, c.name)} className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
                                    <Trash2 className="w-3 h-3" />
                                </Button>
                                <span className="inline-flex items-center gap-1 text-blue-600 font-semibold ml-1"><Eye className="w-3.5 h-3.5" />Se</span>
                            </div>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="col-span-full text-center py-10 text-slate-400">{search ? "Ingen kunder matcher søgningen." : "Ingen data for valgt periode"}</div>
                )}
            </div>

            <CustomerDetailModal
                customer={openCustomer}
                allJobs={jobs}
                allJobDetails={jobDetails}
                open={!!openCustomer}
                onClose={() => setOpenCustomer(null)}
            />

            <CustomerFormDialog open={formOpen} customer={editCustomer} onClose={() => setFormOpen(false)} onSaved={refresh} />
        </div>
    );
}
